import React, { createContext, useContext, useEffect, useState } from "react";

interface DebugOverlayContextType {
    isDebugMode: boolean;
    toggleDebugMode: () => void;
}

const DebugOverlayContext = createContext<DebugOverlayContextType | undefined>(undefined);
export const DebugDepthContext = createContext<number>(0);

/**
 * Hook to use the debug overlay context.
 */
export function useDebugOverlay() {
    const context = useContext(DebugOverlayContext);
    if (!context) {
        throw new Error("useDebugOverlay must be used within a DebugOverlayProvider");
    }
    return context;
}

/**
 * Provider component that listens for Ctrl + ? to toggle debug mode.
 */
export function DebugOverlayProvider({ children }: { children: React.ReactNode }) {
    const [isDebugMode, setIsDebugMode] = useState(false);

    const toggleDebugMode = () => setIsDebugMode((prev) => !prev);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Ctrl + ? (which is often Shift + /)
            if (event.ctrlKey && (event.key === "?" || event.key === "/")) {
                event.preventDefault();
                toggleDebugMode();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <DebugOverlayContext.Provider value={{ isDebugMode, toggleDebugMode }}>
            {children}
        </DebugOverlayContext.Provider>
    );
}
